/**
 * Användaraktivitet view logic: parses `activity/{date}/{actorId}/{entryKey}`
 * and derives the weekly series, the user table and the building breakdown.
 * Pure, no Firebase.
 */
import type { ActivityEntry, ActivityPlatform } from '../../../types/activity';
import { PUBLIC_ACTIVITY_ID } from '../../../types/activity';

export type ActivityRange = '30d' | '3m' | '12m' | '24m';

export const RANGE_OPTIONS: Array<{ value: ActivityRange; label: string }> = [
    { value: '30d', label: '30 dagar' },
    { value: '3m', label: '3 månader' },
    { value: '12m', label: '12 månader' },
    { value: '24m', label: '24 månader' },
];

export const DEFAULT_RANGE: ActivityRange = '3m';

const DATE_KEY = /^\d{4}-\d{2}-\d{2}$/;
const PLATFORMS: readonly ActivityPlatform[] = ['web', 'ios', 'guest', 'public'];

export function parseRange(value: string | null): ActivityRange {
    return RANGE_OPTIONS.some((o) => o.value === value) ? value as ActivityRange : DEFAULT_RANGE;
}

function toDateKey(d: Date): string {
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
}

function parseDateKey(key: string): Date {
    const [y, m, d] = key.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d));
}

/** First date key of the range ending on `today` (inclusive). */
export function rangeStart(range: ActivityRange, today: string): string {
    const d = parseDateKey(today);
    if (range === '30d') d.setUTCDate(d.getUTCDate() - 29);
    else d.setUTCMonth(d.getUTCMonth() - (range === '3m' ? 3 : range === '12m' ? 12 : 24));
    return toDateKey(d);
}

/** Monday (`YYYY-MM-DD`) of the ISO week a date key falls in. */
export function weekStart(dateKey: string): string {
    const d = parseDateKey(dateKey);
    const offset = (d.getUTCDay() + 6) % 7;
    d.setUTCDate(d.getUTCDate() - offset);
    return toDateKey(d);
}

/** Parses the raw `activity` subtree; malformed nodes are skipped. */
export function parseActivity(raw: unknown): ActivityEntry[] {
    if (!raw || typeof raw !== 'object') return [];
    const entries: ActivityEntry[] = [];
    for (const [date, byActor] of Object.entries(raw as Record<string, unknown>)) {
        if (!DATE_KEY.test(date) || !byActor || typeof byActor !== 'object') continue;
        for (const [actorId, byEntry] of Object.entries(byActor as Record<string, unknown>)) {
            if (!byEntry || typeof byEntry !== 'object') continue;
            for (const [key, value] of Object.entries(byEntry as Record<string, unknown>)) {
                if (!value || typeof value !== 'object') continue;
                const v = value as Record<string, unknown>;
                if (typeof v.when !== 'string' || typeof v.action !== 'string') continue;
                const platform = PLATFORMS.includes(v.platform as ActivityPlatform) ? v.platform as ActivityPlatform : 'web';
                entries.push({
                    key,
                    date,
                    actorId,
                    when: v.when,
                    platform,
                    action: v.action,
                    ...(typeof v.buildingKey === 'string' ? { buildingKey: v.buildingKey } : {}),
                    ...(typeof v.targetKey === 'string' ? { targetKey: v.targetKey } : {}),
                    ...(typeof v.label === 'string' ? { label: v.label } : {}),
                    count: typeof v.count === 'number' && v.count > 1 ? v.count : 1,
                });
            }
        }
    }
    return entries;
}

export const isSession = (entry: Pick<ActivityEntry, 'action'>): boolean => entry.action === 'session.started';

/** Actions an entry stands for (sessions are not actions). */
export const actionCount = (entry: ActivityEntry): number => (isSession(entry) ? 0 : entry.count);

/** A row for a person (app or external user), not the public QR report pseudo-actor. */
export const isPersonRow = (row: Pick<UserRow, 'id'>): boolean => row.id !== PUBLIC_ACTIVITY_ID;

/** What the view needs to know about an actor, from `users/userList`. */
export interface ActorInfo {
    id: string;
    name: string;
    superadmin: boolean;
    external: boolean;
    deleted: boolean;
}

export interface WeekPoint {
    week: string;
    activeUsers: number;
    web: number;
    ios: number;
    guest: number;
}

/** One point per ISO week from `start` to `end`, empty weeks included. */
export function weeklySeries(entries: ActivityEntry[], start: string, end: string): WeekPoint[] {
    const weeks = new Map<string, { actors: Set<string>; web: number; ios: number; guest: number }>();
    for (let d = parseDateKey(weekStart(start)); toDateKey(d) <= end; d.setUTCDate(d.getUTCDate() + 7)) {
        weeks.set(toDateKey(d), { actors: new Set(), web: 0, ios: 0, guest: 0 });
    }
    for (const entry of entries) {
        const bucket = weeks.get(weekStart(entry.date));
        if (!bucket) continue;
        if (entry.actorId !== PUBLIC_ACTIVITY_ID) bucket.actors.add(entry.actorId);
        const n = actionCount(entry);
        if (entry.platform === 'ios') bucket.ios += n;
        else if (entry.platform === 'guest' || entry.platform === 'public') bucket.guest += n;
        else bucket.web += n;
    }
    return [...weeks.entries()].map(([week, b]) => ({ week, activeUsers: b.actors.size, web: b.web, ios: b.ios, guest: b.guest }));
}

export interface UserRow {
    id: string;
    name: string;
    external: boolean;
    superadmin: boolean;
    lastActive: string | null;
    activeDays: number;
    platforms: ActivityPlatform[];
    actions: number;
}

/**
 * One row per actor: everyone listed in `users/userList` (inactive ones with
 * zeros, deleted ones only when they have entries) plus anyone else with entries.
 */
export function userRows(entries: ActivityEntry[], actors: Map<string, ActorInfo>): UserRow[] {
    const rows = new Map<string, UserRow & { days: Set<string>; platformSet: Set<ActivityPlatform> }>();
    const rowFor = (id: string) => {
        let row = rows.get(id);
        if (!row) {
            const info = actors.get(id);
            row = {
                id,
                name: info?.name ?? (id === PUBLIC_ACTIVITY_ID ? 'Publik felanmälan' : 'Okänd användare'),
                external: info?.external ?? false,
                superadmin: info?.superadmin ?? false,
                lastActive: null,
                activeDays: 0,
                platforms: [],
                actions: 0,
                days: new Set(),
                platformSet: new Set(),
            };
            rows.set(id, row);
        }
        return row;
    };
    for (const info of actors.values()) {
        if (!info.deleted && !info.external) rowFor(info.id);
    }
    for (const entry of entries) {
        const row = rowFor(entry.actorId);
        row.days.add(entry.date);
        row.platformSet.add(entry.platform);
        row.actions += actionCount(entry);
        if (!row.lastActive || entry.when > row.lastActive) row.lastActive = entry.when;
    }
    return [...rows.values()]
        .map(({ days, platformSet, ...row }) => ({
            ...row,
            activeDays: days.size,
            platforms: PLATFORMS.filter((p) => platformSet.has(p)),
        }));
}

export type UserSortKey = 'name' | 'lastActive' | 'activeDays' | 'actions';

export function sortUserRows(rows: UserRow[], key: UserSortKey): UserRow[] {
    const sorted = [...rows];
    sorted.sort((a, b) => {
        switch (key) {
            case 'name': return a.name.localeCompare(b.name, 'sv');
            case 'lastActive': return (b.lastActive ?? '').localeCompare(a.lastActive ?? '') || a.name.localeCompare(b.name, 'sv');
            case 'activeDays': return b.activeDays - a.activeDays || a.name.localeCompare(b.name, 'sv');
            case 'actions': return b.actions - a.actions || a.name.localeCompare(b.name, 'sv');
        }
        return 0;
    });
    return sorted;
}

export interface BuildingRow {
    buildingKey: string;
    actions: number;
    activeUsers: number;
    lastActive: string;
}

/**
 * Actions and distinct actors per building, most active first. Entries without a
 * building are left out; public QR reports count as actions, never as a user.
 */
export function buildingRows(entries: ActivityEntry[]): BuildingRow[] {
    const rows = new Map<string, { actions: number; actors: Set<string>; lastActive: string }>();
    for (const entry of entries) {
        if (!entry.buildingKey || isSession(entry)) continue;
        let row = rows.get(entry.buildingKey);
        if (!row) {
            row = { actions: 0, actors: new Set(), lastActive: entry.when };
            rows.set(entry.buildingKey, row);
        }
        row.actions += entry.count;
        if (entry.actorId !== PUBLIC_ACTIVITY_ID) row.actors.add(entry.actorId);
        if (entry.when > row.lastActive) row.lastActive = entry.when;
    }
    return [...rows.entries()]
        .map(([buildingKey, r]) => ({ buildingKey, actions: r.actions, activeUsers: r.actors.size, lastActive: r.lastActive }))
        .sort((a, b) => b.actions - a.actions || b.lastActive.localeCompare(a.lastActive));
}

/** An actor's entries, newest first. */
export function actorLog(entries: ActivityEntry[], actorId: string): ActivityEntry[] {
    return entries
        .filter((e) => e.actorId === actorId)
        .sort((a, b) => b.when.localeCompare(a.when) || b.key.localeCompare(a.key));
}

/** Entries whose details live in the Ändringslogg (component, level and room changes). */
export function hasChangeLog(entry: Pick<ActivityEntry, 'action'>): boolean {
    return /^(component|level|room)\./.test(entry.action);
}
